import React from 'react';
import { Skeleton, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import './styles.css';

/**
 * Reusable Table component that handles common table functionality
 * @param {Object} props
 * @param {string} props.title - Table title
 * @param {React.ReactNode} props.titleIcon - Icon for the title
 * @param {Array} props.rowsConfig - Array of row configuration objects with title, className, align, render, customRender
 * @param {Array} props.data - Array of data rows
 * @param {boolean} props.loading - Loading state
 * @param {number} props.loadingRows - Number of loading skeleton rows to show
 * @param {string} props.emptyMessage - Message to show when no data
 * @param {string} props.layout - Table layout class (e.g., 'charges-layout', 'subscription-layout')
 * @param {Function} props.renderRow - Function to render each data row (legacy, will be overridden by rowsConfig)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.enablePagination - Enable pagination functionality
 * @param {number} props.pages - Total number of pages
 * @param {number} props.records - Total number of records
 * @param {number} props.page - Current page
 * @param {Function} props.onSelectPage - Function to handle page selection
 * @param {Function} props.onRowClick - Function to handle row click, receives row data as parameter
 */
const Table = ({
    rowsConfig,
    data,
    loading,
    loadingRows = 4,
    emptyMessage = 'Nenhum dado encontrado',
    layout = '',
    renderRow,
    variant = 'filled',
    className = '',
    enablePagination = false,
    pages = 0,
    records = 0,
    page = 1,
    onSelectPage,
    onRowClick
}) => {
    // Default loading row renderer based on rowsConfig
    const DefaultLoadingRow = () => {
        return (
            <div className="table-row client-info loading-row" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                {rowsConfig.map((config, colIndex) => (
                    <div key={colIndex} className={`table-cell ${config.className || ''}`}>
                        {colIndex === 0 && <Skeleton variant="circular" width={52} height={32} />
                        }
                        <Skeleton variant="text" width={config.loadingWidth || 120} height={20} />
                    </div>
                ))}
            </div>
        );
    };

    // New row renderer based on rowsConfig
    const renderRowFromConfig = (item, index) => {
        if (!rowsConfig) {
            // Fallback to legacy renderRow if rowsConfig is not provided
            return renderRow ? renderRow(item, index) : null;
        }

        const handleRowClick = () => {
            if (onRowClick) {
                onRowClick(item);
            }
        };

        const rowClassName = `table-row ${onRowClick ? 'clickable-row' : ''}`;

        return (
            <div 
                key={item.id || index} 
                className={rowClassName}
                onClick={handleRowClick}
            >
                {rowsConfig.map((config, colIndex) => {
                    const cellClassName = `table-cell ${config.className || ''} ${config.align === 'center' ? 'cell-centered' : ''}`;

                    return (
                        <div key={colIndex} className={cellClassName}>
                            {config.customRender ? (
                                config.customRender(item)
                            ) : config.render ? (
                                <span>{config.render(item)}</span>
                            ) : (
                                <span>{item[config.field] || ''}</span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Pagination component
    const Pagination = () => {
        if (!enablePagination || pages <= 1) return null;

        const handlePageChange = (newPage) => {
            if (onSelectPage && newPage >= 1 && newPage <= pages) {
                onSelectPage(newPage);
            }
        };

        const renderPageButtons = () => {
            const buttons = [];
            const maxVisiblePages = 5;
            let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(pages, startPage + maxVisiblePages - 1);

            // Adjust start page if we're near the end
            if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            // Previous button
            buttons.push(
                <IconButton
                    key="prev"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    className="pagination-button  control-pagination-arrow"
                    size="small"
                >
                    <ChevronLeft />
                </IconButton>
            );

            // First page if not visible
            if (startPage > 1) {
                buttons.push(
                    <IconButton
                        key="first"
                        onClick={() => handlePageChange(1)}
                        className="pagination-button"
                        size="small"
                    >
                        1
                    </IconButton>
                );
                if (startPage > 2) {
                    buttons.push(
                        <span key="dots1" className="pagination-dots">
                            ...
                        </span>
                    );
                }
            }

            // Page numbers
            for (let i = startPage; i <= endPage; i++) {
                buttons.push(
                    <IconButton
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`pagination-button ${i == page ? 'active' : ''}`}
                        size="small"
                    >
                        {i}
                    </IconButton>
                );
            }

            // Last page if not visible
            if (endPage < pages) {
                if (endPage < pages - 1) {
                    buttons.push(
                        <span key="dots2" className="pagination-dots">
                            ...
                        </span>
                    );
                }
                buttons.push(
                    <IconButton
                        key="last"
                        onClick={() => handlePageChange(pages)}
                        className="pagination-button"
                        size="small"
                    >
                        {pages}
                    </IconButton>
                );
            }

            // Next button
            buttons.push(
                <IconButton
                    key="next"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= pages}
                    className="pagination-button  control-pagination-arrow"
                    size="small"
                >
                    <ChevronRight />
                </IconButton>
            );

            return buttons;
        };

        return (
            <div className="table-pagination">
                <div className="pagination-info">
                    Exibindo {data.length} resultados, de {records}
                </div>
                <div className="pagination-controls">
                    {renderPageButtons()}
                </div>
            </div>
        );
    };

    return (
        <div className={`table ${layout} ${className} ${variant}`}>
            <div className="table-header">
                {rowsConfig ? (
                    rowsConfig.map((config, index) => (
                        <div key={index} className={`table-cell ${config.className || ''} ${config.align === 'center' ? 'cell-centered' : ''}`}>
                            {config.title}
                        </div>
                    ))
                ) : (
                    // Fallback for legacy support
                    <div className="table-cell">Column</div>
                )}
            </div>
            <div className="table-body">
                {loading ? (
                    // Show loading skeleton rows
                    [...Array(loadingRows)].map((_, index) => (
                        <DefaultLoadingRow key={`loading-${index}`} />
                    ))
                ) : data.length === 0 ? (
                    // Show empty state
                    <div className="table-row empty-state">
                        <div className="table-cell full-width">
                            {emptyMessage}
                        </div>
                    </div>
                ) : (
                    // Render actual data rows
                    data.map((item, index) => renderRowFromConfig(item, index))
                )}
            </div>
            <Pagination />
        </div>
    );
};

export default Table;
export { Table };
